// admin/src/pages/ProfilePage.tsx
import React, { useEffect, useRef, useState } from 'react';
import {
  Avatar, Box, Button, Card, CardContent, CircularProgress,
  Paper, Stack, TextField, Typography
} from '@mui/material';
import Swal from 'sweetalert2';
import { getAdminProfile, updateAdminProfile } from '../services/api';

type Profile = {
  _id: string;
  fullName?: string;
  username?: string;
  email?: string;
  avatar?: string;
  userType?: string;
};

const ProfilePage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [profile, setProfile] = useState<Profile | null>(null);
  const [fullName, setFullName] = useState('');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');

  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string>('');
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    (async () => {
      try {
        const data = await getAdminProfile();
        setProfile(data || null);
        setFullName(data?.fullName || '');
        setUsername(data?.username || '');
        setEmail(data?.email || '');
        setAvatarPreview(data?.avatar || '');
      } catch {
        Swal.fire('Error', 'Failed to load profile', 'error');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const pickFile = () => fileRef.current?.click();

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith('image/')) {
      Swal.fire('Invalid file', 'Please select an image.', 'warning');
      return;
    }
    setAvatarFile(f);
    const reader = new FileReader();
    reader.onload = () => setAvatarPreview(String(reader.result || ''));
    reader.readAsDataURL(f);
  };

  const save = async () => {
    if (!username.trim() || !email.trim()) {
      Swal.fire('Validation', 'Username and Email are required', 'warning');
      return;
    }
    setSaving(true);
    try {
      await updateAdminProfile({
        fullName: fullName.trim(),
        username: username.trim(),
        email: email.trim(),
        avatar: avatarFile || undefined,
      });
      Swal.fire('Saved', 'Profile updated successfully', 'success');
      const data = await getAdminProfile();
      setProfile(data || null);
      setAvatarPreview(data?.avatar || avatarPreview);
      setAvatarFile(null);
    } catch (e: any) {
      const msg = e?.response?.data?.message || e?.message || 'Failed to update profile';
      Swal.fire('Error', msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={6}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Typography variant="h5" sx={{ mt: 2, mb: 2 }}>My Profile</Typography>

      <Paper sx={{ p: { xs: 2, md: 3 } }}>
        <Stack spacing={3}>
          <Card variant="outlined">
            <CardContent>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar src={avatarPreview || undefined} sx={{ width: 120, height: 120, fontSize: 32 }}>
                    {(fullName || username || 'U').slice(0, 1).toUpperCase()}
                  </Avatar>
                  <Stack spacing={1}>
                    <Button variant="outlined" onClick={pickFile}>
                      {avatarPreview ? 'Change Avatar' : 'Upload Avatar'}
                    </Button>
                    {!!avatarPreview && (
                      <Button variant="text" color="error" onClick={() => { setAvatarFile(null); setAvatarPreview(''); }}>
                        Remove Preview
                      </Button>
                    )}
                  </Stack>
                  <input ref={fileRef} type="file" accept="image/*" hidden onChange={onFileChange} />
                </Box>

                <Box sx={{ flexGrow: 1, minWidth: 260 }}>
                  <Stack spacing={2}>
                    <TextField label="Full Name" fullWidth value={fullName} onChange={(e) => setFullName(e.target.value)} />
                    <TextField label="Role" value={profile?.userType || ''} disabled fullWidth />
                  </Stack>
                </Box>
              </Box>
            </CardContent>
          </Card>

          <Stack spacing={2}>
            <TextField label="Username *" fullWidth value={username} onChange={(e) => setUsername(e.target.value)} />
            <TextField label="Email *" type="email" fullWidth value={email} onChange={(e) => setEmail(e.target.value)} />
          </Stack>

          <Box display="flex" justifyContent="flex-end" gap={2}>
            <Button
              variant="contained"
              onClick={save}
              disabled={saving}
              sx={{
                minWidth: 160,
                background: 'linear-gradient(to right, #6226A7, #F6A938)',
                color: '#fff',
                fontWeight: 600,
                '&:hover': { background: 'linear-gradient(to right, #4e1f84, #db8b21)' },
              }}
            >
              {saving ? 'Saving…' : 'Save Changes'}
            </Button>
          </Box>
        </Stack>
      </Paper>
    </>
  );
};

export default ProfilePage;
